import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewEndorsementKeys, reviewReportKeys } from "@/lib/query";
import type { ReviewEndorsementInput } from "@/schemas/review-endorsements";

export interface ReviewEndorsementRow {
  id: string;
  tenetId: string;
  /** The tenet's name. */
  title: string;
  description: string | null;
  detail: string;
  endorserName: string | null;
  relationshipKey: string | null;
  relationshipName: string | null;
}

/** What this reviewer has endorsed, and how many they have left to spend. */
export interface MyEndorsements {
  cap: number;
  used: number;
  rows: ReviewEndorsementRow[];
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const message =
    body && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  return new Error(message);
}

export function useMyEndorsements(reviewerId: string) {
  return useQuery({
    queryKey: reviewEndorsementKeys.mine(reviewerId),
    queryFn: async (): Promise<MyEndorsements> => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/endorsements`);
      if (!res.ok) throw await failure(res, "Couldn't load your endorsements");
      return (await res.json()).data;
    },
  });
}

export function useAddEndorsement(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewEndorsementInput) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/endorsements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't add that endorsement");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewEndorsementKeys.mine(reviewerId) });
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.all });
      toast.success("Endorsed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useRemoveEndorsement(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/endorsements/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await failure(res, "Couldn't remove that endorsement");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewEndorsementKeys.mine(reviewerId) });
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.all });
      // Said, because the capacity coming back is the point of removing one.
      toast.success("Endorsement removed — that one is free again");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
